/**
 * Utility functions for the customize journey selection state.
 */

import {getCustomizeStepSlotKind} from '@amblique/king-living/app/hooks/use-customize-steps';
import {getProductEffectivePrice, getProductListPrice} from '@amblique/king-living/app/utils/product-utils';

/**
 * Effective price for a product being previewed in the customize flyout.
 * SCAPI option products use promotions via getProductEffectivePrice; cover
 * variants from c_allVariantsData may only expose salePrice/price fields.
 *
 * @param {Object|null|undefined} previewProduct
 * @returns {number|null}
 */
function resolvePreviewEffectivePrice(previewProduct) {
  if (!previewProduct) return null;

  const effective = getProductEffectivePrice(previewProduct);

  if (effective != null) return effective;

  const fallback =
    previewProduct.salePrice ??
    previewProduct.price ??
    previewProduct.priceRanges?.[0]?.minPrice ??
    null;

  return fallback != null ? fallback : null;
}

/** Shelves footer/slot line: PIM "A - B" → "A, B" (matches SlotCard). */
function formatShelvesSelectionName(name) {
  if (typeof name !== 'string') return '';

  return name.replace(/\s*-\s*/g, ', ');
}

/**
 * Comma-joined titles for visible slots with a selection (partial OK).
 *
 * @param {Object} step - buildSteps() descriptor
 * @param {Object} selections
 * @returns {string}
 */
export function joinStepSlotSelectionTitles(step, selections) {
  if (!step?.container?.values) return '';

  const stepSelections = selections?.[step.id] || [];
  const isShelves = getCustomizeStepSlotKind(step) === 'shelves';
  const parts = [];

  step.container.values.forEach((slotMeta, i) => {
    if (slotMeta?.isVisible === false) return;

    const name = stepSelections[i]?.name;

    if (!name) return;

    const label = isShelves ? formatShelvesSelectionName(name) : name;

    if (label) parts.push(label);
  });

  return parts.join(', ');
}

/**
 * Sum prices from a selections map.
 * Adds cover.salePrice + each slot's price across all option containers.
 * Only positive prices are counted.
 *
 * @param {Object} selectionsMap
 * @returns {number|null} Total price, or null when no priced items exist
 */
export function sumSelectionPrices(selectionsMap) {
  if (!selectionsMap || typeof selectionsMap !== 'object') {
    return null;
  }

  let total = 0;
  let hasPrices = false;

  const coverPrice = selectionsMap.cover?.salePrice;
  const coverNum = typeof coverPrice === 'number' ? coverPrice : Number(coverPrice);

  if (Number.isFinite(coverNum) && coverNum > 0) {
    total += coverNum;
    hasPrices = true;
  }

  Object.keys(selectionsMap)
    // 'cover' is handled above; 'optionalAddOns' selections are excluded from the
    // "Customize your own" trigger total — they are tracked separately for add-to-cart.
    .filter((k) => k !== 'cover' && k !== 'optionalAddOns')
    .forEach((key) => {
      const slots = selectionsMap[key];

      if (!Array.isArray(slots)) return;

      slots.forEach((slot) => {
        const raw = slot?.price ?? slot?.priceDelta;
        const n = typeof raw === 'number' ? raw : Number(raw);

        if (Number.isFinite(n) && n > 0) {
          total += n;
          hasPrices = true;
        }
      });
    });

  return hasPrices ? total : null;
}

/**
 * Sum list prices from a selections map (for strikethrough display).
 * Uses cover.price (list price) + each slot's listPrice across all option containers.
 * Returns 0 when no component carries a genuine list price (i.e. no discount exists).
 *
 * @param {Object} selectionsMap
 * @returns {number}
 */
export function sumSelectionListPrices(selectionsMap) {
  if (!selectionsMap || typeof selectionsMap !== 'object') return 0;

  let total = 0;
  let hasListData = false;

  const cover = selectionsMap.cover;

  if (cover) {
    const listP = cover.price;
    const saleP = cover.salePrice;

    if (listP != null && saleP != null && listP > saleP) {
      total += listP;
      hasListData = true;
    } else {
      const raw = saleP ?? listP;
      const n = typeof raw === 'number' ? raw : Number(raw);

      if (Number.isFinite(n) && n > 0) total += n;
    }
  }

  Object.keys(selectionsMap)
    .filter((k) => k !== 'cover' && k !== 'optionalAddOns')
    .forEach((key) => {
      const slots = selectionsMap[key];

      if (!Array.isArray(slots)) return;

      slots.forEach((slot) => {
        if (slot?.listPrice != null && slot.listPrice > (slot?.price ?? 0)) {
          total += slot.listPrice;
          hasListData = true;
        } else {
          const raw = slot?.price ?? slot?.priceDelta;
          const n = typeof raw === 'number' ? raw : Number(raw);

          if (Number.isFinite(n) && n > 0) total += n;
        }
      });
    });

  return hasListData ? total : 0;
}

/**
 * Compute a live subtotal that substitutes the previewed product's price for the
 * active slot, so the footer updates in real-time as the user browses products
 * before pressing "Next".
 *
 * - For cover slots: replaces cover.salePrice with the preview product's effective price.
 * - For option slots: replaces the slot entry at activeSlot.slotIndex with the
 *   preview product's effective price (promotion-aware, same as buildOptionSlotSelectionFromProduct).
 * - Falls back to sumSelectionPrices(selectionsMap) when no preview is active.
 *
 * @param {Object} selectionsMap - Current committed draft selections
 * @param {{stepId: string, slotIndex: number}|null} activeSlot - Currently open slot
 * @param {Object|null} previewProduct - Product being previewed (from galleryPreviewProduct)
 * @param {number|null} masterMinPrice - Fallback price when nothing is priced
 * @returns {number|null}
 */
export function computeLiveSubtotal(selectionsMap, activeSlot, previewProduct, masterMinPrice) {
  if (!activeSlot || !previewProduct) {
    return sumSelectionPrices(selectionsMap) ?? masterMinPrice;
  }

  const previewPrice = resolvePreviewEffectivePrice(previewProduct);

  if (activeSlot.stepId === 'cover') {
    const patched = {
      ...selectionsMap,
      cover: {...(selectionsMap?.cover ?? {}), salePrice: previewPrice},
    };

    return sumSelectionPrices(patched) ?? masterMinPrice;
  }

  // Option slot: patch the specific slot index with the preview price
  const existingSlots = selectionsMap?.[activeSlot.stepId] || [];
  const patchedSlots = [...existingSlots];

  patchedSlots[activeSlot.slotIndex] = {
    ...(patchedSlots[activeSlot.slotIndex] ?? {}),
    price: previewPrice,
  };

  const patched = {
    ...selectionsMap,
    [activeSlot.stepId]: patchedSlots,
  };

  return sumSelectionPrices(patched) ?? masterMinPrice;
}

/**
 * List-total counterpart to {@link computeLiveSubtotal} for footer strikethrough while previewing.
 *
 * @param {Object} selectionsMap
 * @param {{stepId: string, slotIndex: number}|null} activeSlot
 * @param {Object|null} previewProduct
 * @returns {number}
 */
export function computeLiveListSubtotal(selectionsMap, activeSlot, previewProduct) {
  if (!activeSlot || !previewProduct) {
    return sumSelectionListPrices(selectionsMap);
  }

  const previewSale = resolvePreviewEffectivePrice(previewProduct);
  const previewList = getProductListPrice(previewProduct);

  if (activeSlot.stepId === 'cover') {
    const patched = {
      ...selectionsMap,
      cover: {
        ...(selectionsMap?.cover ?? {}),
        salePrice: previewSale,
        price: previewList != null && previewSale != null && previewList > previewSale ?
          previewList :
          selectionsMap?.cover?.price ?? null,
      },
    };

    return sumSelectionListPrices(patched);
  }

  const existingSlots = selectionsMap?.[activeSlot.stepId] || [];
  const patchedSlots = [...existingSlots];

  patchedSlots[activeSlot.slotIndex] = {
    ...(patchedSlots[activeSlot.slotIndex] ?? {}),
    price: previewSale,
    listPrice: previewList,
  };

  return sumSelectionListPrices({
    ...selectionsMap,
    [activeSlot.stepId]: patchedSlots,
  });
}
